import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
    AuctionCatalogsService,
    GetPendingPickupActions200Response,
    GetPendingPickupActions200ResponseAllOfDataFlaggedInvoicesInner,
} from '@viewbid/ts-angular-client';
import { Store } from '@ngrx/store';
import { User } from 'viewbid-shared-lib';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MasqueradeService } from './masquerade.service';
import { AppState } from '../../state/app-state';
import { selectUser } from '../../state/app-selectors';

export type PendingInvoice =
    Required<GetPendingPickupActions200ResponseAllOfDataFlaggedInvoicesInner>;

@Injectable({ providedIn: 'root' })
export class PendingPickupService {
    private readonly pendingSubject = new BehaviorSubject<PendingInvoice[]>([]);
    private checkedForUserId: string | null = null;
    private modalRef?: BsModalRef;

    readonly pendingInvoices$: Observable<PendingInvoice[]> =
        this.pendingSubject.asObservable();

    constructor(
        private readonly auctionCatalogsService: AuctionCatalogsService,
        private readonly masqueradeService: MasqueradeService,
        private readonly store: Store<AppState>,
        private readonly modalService: BsModalService,
    ) {}

    // Opens the blocking forfeiture modal if there are pending invoices and no
    // modal is already open. Centralized here so the guard and socket handler
    // can't race and produce two modals.
    openModalIfPending(modalComponent: Type<unknown>): void {
        if (this.modalRef) return;
        if (!this.hasPending()) return;

        this.modalRef = this.modalService.show(modalComponent, {
            class: 'modal-md custom-modal-width modal-dialog-centered',
            ignoreBackdropClick: true,
            keyboard: false,
        });

        this.modalRef.onHidden?.pipe(take(1)).subscribe(() => {
            this.modalRef = undefined;
        });
    }

    // Waits for the current user to be hydrated, then runs checkPendingActions
    // once per user. Safe to call from multiple entry points (guard, post-login,
    // socket handler) — only the first call per session hits the API.
    async ensureChecked(): Promise<void> {
        if (this.masqueradeService.isMasquerading()) return;

        const user: User = await firstValueFrom(
            this.store.select(selectUser).pipe(
                filter((u: User | null): u is User => !!u?.accountMappingsId),
                take(1),
            ),
        );

        const userId: string = user.accountMappingsId ?? '';
        if (this.checkedForUserId === userId) return;
        this.checkedForUserId = userId;

        await this.checkPendingActions();
    }

    async checkPendingActions(): Promise<void> {
        if (this.masqueradeService.isMasquerading()) return;

        try {
            const response: GetPendingPickupActions200Response =
                await firstValueFrom(
                    this.auctionCatalogsService.getPendingPickupActions(),
                );
            const invoices: PendingInvoice[] = (response?.data
                ?.flaggedInvoices ?? []) as PendingInvoice[];
            this.pendingSubject.next(invoices);
        } catch {
            this.pendingSubject.next([]);
        }
    }

    acknowledgeForfeiture(invoiceId: string): Observable<void> {
        return this.auctionCatalogsService
            .acknowledgePickupForfeiture(invoiceId)
            .pipe(
                map(() => {
                    this.removePendingInvoice(invoiceId);
                }),
            );
    }

    removePendingInvoice(invoiceId: string): void {
        const current: PendingInvoice[] = this.pendingSubject.value;
        this.pendingSubject.next(
            current.filter((inv: PendingInvoice) => inv.id !== invoiceId),
        );
    }

    addFromSocket(invoiceIds: string[]): void {
        const existing: PendingInvoice[] = this.pendingSubject.value;
        const existingIds: Set<string> = new Set(
            existing.map((inv: PendingInvoice) => inv.id),
        );
        const newEntries: PendingInvoice[] = invoiceIds
            .filter((id: string) => !existingIds.has(id))
            .map((id: string) => ({
                id,
                auctionName: '',
                pickupFlaggedAt: new Date().toISOString(),
            }));

        if (newEntries.length > 0) {
            this.pendingSubject.next([...existing, ...newEntries]);
            // Re-fetch to get proper auction names
            this.checkPendingActions();
        }
    }

    hasPending(): boolean {
        return this.pendingSubject.value.length > 0;
    }

    // Re-emits the current pending list so the modal subscription in
    // AppComponent fires again. Used by the route guard to re-open the modal
    // after the user dismissed it (e.g. clicked Schedule, then navigated back
    // without completing).
    republishIfPending(): void {
        if (this.hasPending()) {
            this.pendingSubject.next(this.pendingSubject.value);
        }
    }
}
